import { useEffect, useRef } from 'react';
import { AuthenticatedPhoto } from '@/components/photographs/AuthenticatedPhoto';
import { rowSerial } from '@/lib/row-serial';
import { compareWorkerCategoryBases, normalizeWorkerCategoryBase } from '@/lib/worker-category';
import type { BlockReportPhoto } from '@/lib/types';

export { normalizeWorkerCategoryBase } from '@/lib/worker-category';

const UNASSIGNED_LABEL = 'UNASSIGNED';
const UNASSIGNED_BLOCK_NAME = 'No Block / Unassigned';

export interface ProjectBlock {
  id: string;
  name: string;
}

function formatRevisedAt(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

function mergeReportTexts(values: (string | null | undefined)[]): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const s = String(value || '').replace(/\s+/g, ' ').trim();
    if (!s) continue;
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out.join('; ');
}

interface PendingBlockRecord {
  id: string;
  blockId: string | null;
  blockName: string | null;
}

interface PendingActualRecord extends PendingBlockRecord {
  actualNumber?: string | null;
  revisionNumber?: number;
  revisedBy?: string | null;
  revisedAt?: string | null;
  status?: string | null;
  actualSkilled: number;
  actualSemiSkilled?: number;
  actualUnskilled: number;
  typeOfWork: string | null;
  locationOfWork: string | null;
  descriptionOfWork: string | null;
  actualWorkDone: string | null;
  remarks: string | null;
  photos: BlockReportPhoto[];
  workDate: string | null;
  totalUnits?: number;
  managerDecidedUnitsCount?: number;
  clusterDecidedUnitsCount?: number;
  workers?: Array<{
    id?: string | null;
    categoryId?: string | null;
    categoryName?: string | null;
    skillLevel?: string | null;
    count?: number | null;
    totalUnits?: number | null;
    workerUnits?: Array<{
      unitIndex: number;
      managerStatus: string;
      clusterStatus: string;
      overallStatus: string;
      managerRemarks?: string | null;
      clusterRemarks?: string | null;
    }> | null;
  }> | null;
}

interface BlockGroup<T> {
  key: string;
  label: string | null;
  records: T[];
}

function groupByBlock<T extends PendingBlockRecord>(items: T[], projectBlocks?: ProjectBlock[]): BlockGroup<T>[] {
  const raw = new Map<string, T[]>();
  for (const item of items) {
    const key = item.blockId || '__unassigned__';
    const list = raw.get(key) || [];
    list.push(item);
    raw.set(key, list);
  }

  const unassigned: T[] = [];
  const byBlockId = new Map<string, T[]>();
  for (const [key, list] of raw) {
    if (key === '__unassigned__') {
      unassigned.push(...list);
    } else {
      byBlockId.set(key, list);
    }
  }
  const nameById = new Map((projectBlocks ?? []).map((b) => [b.id, b.name]));
  const groups: BlockGroup<T>[] = [];
  for (const [blockId, list] of byBlockId) {
    const blockName = nameById.get(blockId) ?? list[0].blockName ?? blockId;
    groups.push({ key: blockId, label: blockName, records: list });
  }
  groups.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  if (unassigned.length > 0) {
    groups.push({ key: UNASSIGNED_LABEL, label: null, records: unassigned });
  }
  return groups;
}

export type WorkerSkillBucket = 'SKILLED' | 'SEMI_SKILLED' | 'UNSKILLED';

export interface RequirementWorkerUnit {
  unitKey: string;
  itemId: string;
  requirementId: string;
  unitIndex: number;
  categoryName: string;
  categoryBaseName: string;
  displayCategoryName: string;
  categoryCount: number;
  skillBucket: WorkerSkillBucket;
  isApproved: boolean;
  managerStatus: string;
  clusterStatus: string;
  overallStatus: string;
  managerRemarks: string | null;
  clusterRemarks: string | null;
}

export interface ApprovalRequirementRow {
  key: string;
  requirementId: string;
  requirementNumber: string;
  revisionNumber?: number;
  revisedBy?: string | null;
  revisedAt?: string | null;
  workDate: string;
  blockName: string;
  locationOfWork: string;
  descriptionOfWork: string;
  descriptionLines: string[];
  workerUnits: RequirementWorkerUnit[];
  recordIds: string[];
  requiredSkilled: number;
  requiredSemiSkilled: number;
  requiredUnskilled: number;
  totalUnits: number;
  managerDecidedUnitsCount: number;
  clusterDecidedUnitsCount: number;
  allUnitsApproved: boolean;
  hasData: boolean;
}

export interface ActualWorkerUnit {
  unitKey: string;
  actualStatusId: string;
  workerId: string;
  unitIndex: number;
  categoryName: string;
  categoryCount: number;
  skillBucket: 'SKILLED' | 'SEMI_SKILLED' | 'UNSKILLED';
  isApproved: boolean;
  managerStatus: string;
  clusterStatus: string;
  overallStatus: string;
  managerRemarks: string | null;
  clusterRemarks: string | null;
}

export interface ApprovalActualRow {
  key: string;
  actualId: string;
  actualNumber: string;
  revisionNumber?: number;
  revisedBy?: string | null;
  revisedAt?: string | null;
  blockName: string;
  recordIds: string[];
  workDate: string;
  actualSkilled: number;
  actualSemiSkilled: number;
  actualUnskilled: number;
  typeOfWork: string;
  locationOfWork: string;
  descriptionOfWork: string;
  descriptionLines: string[];
  actualWorkDone: string;
  remarks: string;
  photos: BlockReportPhoto[];
  workerUnits: ActualWorkerUnit[];
  totalUnits: number;
  managerDecidedUnitsCount: number;
  clusterDecidedUnitsCount: number;
  allUnitsApproved: boolean;
  hasData: boolean;
}

export function cleanCategoryName(name: string | null | undefined): string {
  return normalizeWorkerCategoryBase(name);
}

function normalizeWorkerCategoryKey(name: string): string {
  return normalizeWorkerCategoryBase(name).replace(/[\s_-]+/g, ' ').trim().toLowerCase() || 'labour';
}

interface GroupableWorkerUnit {
  categoryName: string;
  categoryBaseName?: string;
  skillBucket: WorkerSkillBucket;
}

export interface WorkerCategoryGroup<T extends GroupableWorkerUnit> {
  key: string;
  baseName: string;
  unitsBySkill: Record<WorkerSkillBucket, T[]>;
}

export function groupWorkerUnitsByCategory<T extends GroupableWorkerUnit>(units: T[]): WorkerCategoryGroup<T>[] {
  const groups = new Map<string, WorkerCategoryGroup<T>>();
  for (const unit of units) {
    const baseName = normalizeWorkerCategoryBase(unit.categoryBaseName || unit.categoryName);
    const key = normalizeWorkerCategoryKey(baseName);
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        baseName,
        unitsBySkill: { SKILLED: [], SEMI_SKILLED: [], UNSKILLED: [] },
      };
      groups.set(key, group);
    }
    group.unitsBySkill[unit.skillBucket].push(unit);
  }

  return Array.from(groups.values()).sort((a, b) => {
    const nameCmp = compareWorkerCategoryBases(a.baseName, b.baseName);
    return nameCmp !== 0 ? nameCmp : a.key.localeCompare(b.key);
  });
}

function resolveWorkerSkillBucket(skillLevel?: string | null, catName?: string | null): WorkerSkillBucket {
  const skill = (skillLevel || '').replace(/[\s_-]+/g, '').toUpperCase();
  const name = (catName || '').toUpperCase();
  if (skill === 'SEMISKILLED') return 'SEMI_SKILLED';
  if (skill === 'UNSKILLED') return 'UNSKILLED';
  if (skill === 'SKILLED') return 'SKILLED';
  if (name.includes('SEMI')) return 'SEMI_SKILLED';
  if (name.includes('UNSKILLED') || name.includes('UN-SKILLED') || name.includes('UN SKILLED') || name.includes('HELPER') || name.includes('LABOURER')) return 'UNSKILLED';
  if (name.includes('SKILLED') || name.includes('MASON') || name.includes('CARPENTER') || name.includes('ELECTRICIAN') || name.includes('PLUMBER') || name.includes('BARBENDER')) return 'SKILLED';
  return 'UNSKILLED';
}

function splitDescriptionLines(desc: string | null | undefined): string[] {
  if (!desc) return [];
  let lines = desc.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  if (lines.length <= 1 && desc.includes(';')) {
    const semi = desc.split(/;\s*/).map((s) => s.trim()).filter(Boolean);
    if (semi.length > 1) {
      lines = semi;
    }
  }
  return lines;
}

export function buildRequirementRows(
  items: any[],
  projectBlocks?: ProjectBlock[],
  stage: 'manager' | 'cluster' = 'manager',
): ApprovalRequirementRow[] {
  const blockNameMap = new Map((projectBlocks ?? []).map((b) => [b.id, b.name]));
  const rows: ApprovalRequirementRow[] = [];

  for (const r of items) {
    const blockName = (r.blockId ? blockNameMap.get(r.blockId) : null) ?? r.blockName ?? UNASSIGNED_BLOCK_NAME;
    const workerUnits: RequirementWorkerUnit[] = [];

    // Each saved line item stores an aggregate headcount. Expand it into one
    // independently selectable worker entry per required head so a PM can
    // approve one Mason without approving the others.
    if (Array.isArray(r.items) && r.items.length > 0) {
      for (const item of r.items) {
        const count = Math.max(0, Number(item.count) || 0);
        const totalUnits = Math.max(0, Number(item.totalUnits) || count);
        const bucket = resolveWorkerSkillBucket(item.skillLevel, item.workerCategoryName);
        const catName = cleanCategoryName(item.workerCategoryName);
        const displayCategoryName = String(item.workerCategoryName || '').replace(/\s+/g, ' ').trim() || catName;
        if (Array.isArray(item.workerUnits)) {
          for (const u of item.workerUnits) {
            workerUnits.push({
              unitKey: `item:${r.id}:${item.id}:${u.unitIndex}`,
              itemId: item.id,
              requirementId: r.id,
              unitIndex: Number(u.unitIndex) || 0,
              categoryName: catName,
              categoryBaseName: catName,
              displayCategoryName,
              categoryCount: totalUnits,
              skillBucket: bucket,
              isApproved: u.overallStatus === 'APPROVED',
              managerStatus: u.managerStatus || 'PENDING',
              clusterStatus: u.clusterStatus || (u.managerStatus === 'APPROVED' ? 'PENDING' : 'WAITING_FOR_PM'),
              overallStatus: u.overallStatus || 'PENDING_MANAGER',
              managerRemarks: u.managerRemarks ?? null,
              clusterRemarks: u.clusterRemarks ?? null,
            });
          }
          continue;
        }
        const approvedUnits = Array.isArray(item.approvedUnits) ? item.approvedUnits : [];
        for (let u = 0; u < count; u++) {
          const approved = approvedUnits.includes(u);
          workerUnits.push({
            unitKey: `item:${r.id}:${item.id}:${u}`,
            itemId: item.id,
            requirementId: r.id,
            unitIndex: u,
            categoryName: catName,
            categoryBaseName: catName,
            displayCategoryName,
            categoryCount: count,
            skillBucket: bucket,
            isApproved: approved,
            managerStatus: approved ? 'APPROVED' : 'PENDING',
            clusterStatus: approved ? 'PENDING' : 'WAITING_FOR_PM',
            overallStatus: approved ? 'PENDING_CLUSTER' : 'PENDING_MANAGER',
            managerRemarks: null,
            clusterRemarks: null,
          });
        }
      }
    }

    // The report has separate SKILLED, SEMI-SKILLED and UN-SKILLED columns.
    // Keep each bucket together while preserving the saved item order inside
    // each block.
    const skillOrder: Record<'SKILLED' | 'SEMI_SKILLED' | 'UNSKILLED', number> = { SKILLED: 0, SEMI_SKILLED: 1, UNSKILLED: 2 };
    workerUnits.sort((a, b) => {
      const bucketCmp = skillOrder[a.skillBucket] - skillOrder[b.skillBucket];
      if (bucketCmp !== 0) return bucketCmp;
      const itemCmp = a.itemId.localeCompare(b.itemId);
      if (itemCmp !== 0) return itemCmp;
      return a.unitIndex - b.unitIndex;
    });

    const descLines = splitDescriptionLines(r.descriptionOfWork);
    const allUnitsApproved = workerUnits.length > 0 && workerUnits.every((u) => u.isApproved);
    const itemTotalUnits = (Array.isArray(r.items) ? r.items : []).reduce((sum: number, item: any) => {
      return sum + Math.max(0, Number(item.totalUnits) || Number(item.count) || 0);
    }, 0);
    const totalUnits = Math.max(Number(r.totalUnits) || 0, itemTotalUnits, workerUnits.length);
    const managerDecidedFromItems = (Array.isArray(r.items) ? r.items : []).reduce((sum: number, item: any) => {
      return sum + (Number(item.managerApprovedUnitsCount) || 0) + (Number(item.managerRejectedUnitsCount) || 0);
    }, 0);
    const clusterDecidedFromItems = (Array.isArray(r.items) ? r.items : []).reduce((sum: number, item: any) => {
      return sum + (Number(item.clusterApprovedUnitsCount) || 0) + (Number(item.clusterRejectedUnitsCount) || 0);
    }, 0);
    const managerDecidedFromUnits = workerUnits.filter((u) => u.managerStatus === 'APPROVED' || u.managerStatus === 'REJECTED').length;
    const clusterDecidedFromUnits = workerUnits.filter((u) => u.clusterStatus === 'APPROVED' || u.clusterStatus === 'REJECTED').length;
    const managerDecidedUnitsCount = Math.max(Number(r.managerDecidedUnitsCount) || 0, managerDecidedFromItems, managerDecidedFromUnits);
    const clusterDecidedUnitsCount = Math.max(Number(r.clusterDecidedUnitsCount) || 0, clusterDecidedFromItems, clusterDecidedFromUnits);
    const selectableUnitKeys = workerUnits
      .filter((u) => stage === 'cluster'
        ? u.managerStatus === 'APPROVED' && u.clusterStatus === 'PENDING'
        : u.managerStatus !== 'APPROVED' && u.managerStatus !== 'REJECTED')
      .map((u) => u.unitKey);

    rows.push({
      key: r.id,
      requirementId: r.id,
      requirementNumber: r.requirementNumber || r.id,
      revisionNumber: Number(r.revisionNumber) || 0,
      revisedBy: r.revisedBy ?? null,
      revisedAt: r.revisedAt ?? null,
      workDate: r.workDate || '',
      blockName,
      locationOfWork: r.locationOfWork || '',
      descriptionOfWork: r.descriptionOfWork || '',
      descriptionLines: descLines,
      workerUnits,
      recordIds: selectableUnitKeys,
      requiredSkilled: workerUnits.filter((u) => u.skillBucket === 'SKILLED').length,
      requiredSemiSkilled: workerUnits.filter((u) => u.skillBucket === 'SEMI_SKILLED').length,
      requiredUnskilled: workerUnits.filter((u) => u.skillBucket === 'UNSKILLED').length,
      totalUnits,
      managerDecidedUnitsCount,
      clusterDecidedUnitsCount,
      allUnitsApproved,
      hasData: true,
    });
  }

  // Requirements are created with a single work date, so each date is its own
  // independent row/approval target. Sort by date first so date-wise groups
  // stay contiguous even when several blocks share a date.
  rows.sort((a, b) => {
    const dateCmp = (a.workDate || '').localeCompare(b.workDate || '');
    if (dateCmp !== 0) return dateCmp;
    const blockCmp = (a.blockName || '').localeCompare(b.blockName || '');
    if (blockCmp !== 0) return blockCmp;
    return (a.requirementNumber || '').localeCompare(b.requirementNumber || '');
  });

  return rows;
}

function uniquePhotos(records: PendingActualRecord[]): BlockReportPhoto[] {
  const seen = new Set<string>();
  const out: BlockReportPhoto[] = [];
  for (const record of records) {
    for (const photo of record.photos || []) {
      if (seen.has(photo.photo_id)) continue;
      seen.add(photo.photo_id);
      out.push(photo);
    }
  }
  return out;
}

export function buildActualRows(items: PendingActualRecord[], projectBlocks?: ProjectBlock[]): ApprovalActualRow[] {
  const blockNameMap = new Map((projectBlocks ?? []).map((b) => [b.id, b.name]));
  const rows: ApprovalActualRow[] = [];

  for (const r of items) {
    const blockName = (r.blockId ? blockNameMap.get(r.blockId) : null) ?? r.blockName ?? UNASSIGNED_BLOCK_NAME;
    const workerUnits: ActualWorkerUnit[] = [];

    // Each saved worker row is an aggregate headcount. Expand it into one
    // independently selectable entry per head, exactly like the requirement
    // approval sheet. The actual status header is kept as its own row (rather
    // than merging every record for a block) so another date's entries can
    // never be approved with this date.
    (Array.isArray(r.workers) ? r.workers : []).forEach((worker, workerIndex) => {
      const workerId = worker.id || `${r.id}:worker:${workerIndex}`;
      const count = Math.max(0, Number(worker.count) || 0);
      const totalUnits = Math.max(0, Number(worker.totalUnits) || count);
      const bucket = resolveWorkerSkillBucket(worker.skillLevel, worker.categoryName);
      const categoryName = cleanCategoryName(worker.categoryName);
      if (Array.isArray(worker.workerUnits) && worker.workerUnits.length > 0) {
        for (const unit of worker.workerUnits) {
          const unitIndex = Number(unit.unitIndex) || 0;
          workerUnits.push({
            unitKey: `worker:${r.id}:${workerId}:${unitIndex}`,
            actualStatusId: r.id,
            workerId,
            unitIndex,
            categoryName,
            categoryCount: Math.max(totalUnits, unitIndex + 1),
            skillBucket: bucket,
            isApproved: unit.overallStatus === 'APPROVED',
            managerStatus: unit.managerStatus || 'PENDING',
            clusterStatus: unit.clusterStatus || (unit.managerStatus === 'APPROVED' ? 'PENDING' : 'WAITING_FOR_PM'),
            overallStatus: unit.overallStatus || 'PENDING_MANAGER',
            managerRemarks: unit.managerRemarks ?? null,
            clusterRemarks: unit.clusterRemarks ?? null,
          });
        }
        return;
      }
      for (let unitIndex = 0; unitIndex < count; unitIndex++) {
        workerUnits.push({
          unitKey: `worker:${r.id}:${workerId}:${unitIndex}`,
          actualStatusId: r.id,
          workerId,
          unitIndex,
          categoryName,
          categoryCount: count,
          skillBucket: bucket,
          isApproved: false,
          managerStatus: 'PENDING',
          clusterStatus: 'WAITING_FOR_PM',
          overallStatus: 'PENDING_MANAGER',
          managerRemarks: null,
          clusterRemarks: null,
        });
      }
    });

    const skillOrder: Record<'SKILLED' | 'SEMI_SKILLED' | 'UNSKILLED', number> = { SKILLED: 0, SEMI_SKILLED: 1, UNSKILLED: 2 };
    workerUnits.sort((a, b) => {
      const bucketCmp = skillOrder[a.skillBucket] - skillOrder[b.skillBucket];
      if (bucketCmp !== 0) return bucketCmp;
      const workerCmp = a.workerId.localeCompare(b.workerId);
      if (workerCmp !== 0) return workerCmp;
      return a.unitIndex - b.unitIndex;
    });

    const actualSkilled = Number(r.actualSkilled) || workerUnits.filter((u) => u.skillBucket === 'SKILLED').length;
    const actualSemiSkilled = Number(r.actualSemiSkilled) || workerUnits.filter((u) => u.skillBucket === 'SEMI_SKILLED').length;
    const actualUnskilled = Number(r.actualUnskilled) || workerUnits.filter((u) => u.skillBucket === 'UNSKILLED').length;
    const totalUnits = Math.max(Number(r.totalUnits) || 0, workerUnits.length);
    const managerDecidedFromUnits = workerUnits.filter((u) => u.managerStatus === 'APPROVED' || u.managerStatus === 'REJECTED').length;
    const clusterDecidedFromUnits = workerUnits.filter((u) => u.clusterStatus === 'APPROVED' || u.clusterStatus === 'REJECTED').length;
    const managerDecidedUnitsCount = Math.max(Number(r.managerDecidedUnitsCount) || 0, managerDecidedFromUnits);
    const clusterDecidedUnitsCount = Math.max(Number(r.clusterDecidedUnitsCount) || 0, clusterDecidedFromUnits);
    const descLines = splitDescriptionLines(r.descriptionOfWork);
    const actualNumber = r.actualNumber || r.id;

    rows.push({
      key: r.id,
      actualId: r.id,
      actualNumber,
      revisionNumber: Number(r.revisionNumber) || 0,
      revisedBy: r.revisedBy ?? null,
      revisedAt: r.revisedAt ?? null,
      blockName,
      recordIds: [r.id],
      workDate: r.workDate || '',
      actualSkilled,
      actualSemiSkilled,
      actualUnskilled,
      typeOfWork: r.typeOfWork || '',
      locationOfWork: r.locationOfWork || '',
      descriptionOfWork: r.descriptionOfWork || '',
      descriptionLines: descLines,
      actualWorkDone: r.actualWorkDone || '',
      remarks: r.remarks || '',
      photos: r.photos || [],
      workerUnits,
      totalUnits,
      managerDecidedUnitsCount,
      clusterDecidedUnitsCount,
      allUnitsApproved: workerUnits.length > 0 && workerUnits.every((u) => u.isApproved),
      hasData: true,
    });
  }

  // Each actual status header is one approval target. Dates sort first so a
  // date's entries stay together, while separate dates can be reviewed and
  // approved independently.
  rows.sort((a, b) => {
    const dateCmp = (a.workDate || '').localeCompare(b.workDate || '');
    if (dateCmp !== 0) return dateCmp;
    const blockCmp = (a.blockName || '').localeCompare(b.blockName || '');
    if (blockCmp !== 0) return blockCmp;
    return (a.actualNumber || '').localeCompare(b.actualNumber || '');
  });

  return rows;
}

function ApproveCheckbox({
  checked,
  indeterminate,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  label: string;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      aria-label={label}
      onChange={onChange}
      className="h-4 w-4 cursor-pointer align-middle"
    />
  );
}

interface ApprovalSheetProps {
  projectName: string;
  selectedIds: Set<string>;
  onToggleRecordIds: (recordIds: string[]) => void;
  allSelected: boolean;
  partial: boolean;
  onToggleAll: () => void;
  stage?: 'manager' | 'cluster';
}

function numberCell(value: number): string {
  return Number.isFinite(value) ? String(value) : '';
}

function workerUnitLabel(unit: RequirementWorkerUnit): string {
  return `${unit.displayCategoryName || unit.categoryName} #${unit.unitIndex + 1} of ${Math.max(unit.categoryCount, unit.unitIndex + 1)}`;
}

function actualWorkerUnitLabel(unit: ActualWorkerUnit): string {
  return `${unit.categoryName} #${unit.unitIndex + 1} of ${Math.max(unit.categoryCount, unit.unitIndex + 1)}`;
}

function statusLabel(status: string): string {
  if (status === 'APPROVED') return 'Approved';
  if (status === 'REJECTED') return 'Rejected';
  if (status === 'WAITING_FOR_PM') return 'Waiting for PM';
  if (status === 'NOT_REQUIRED') return 'Not required';
  return 'Pending';
}

function stageStatusLabel(stage: 'manager' | 'cluster', status: string): string {
  if (status === 'APPROVED') return stage === 'manager' ? 'Approved by PM' : 'Approved by CH';
  if (status === 'REJECTED') return stage === 'manager' ? 'Rejected by PM' : 'Rejected by CH';
  return statusLabel(status);
}

function statusClass(status: string): string {
  if (status === 'APPROVED') return 'text-emerald-600 dark:text-emerald-400';
  if (status === 'REJECTED') return 'text-red-600 dark:text-red-400';
  if (status === 'WAITING_FOR_PM') return 'text-muted-foreground';
  return 'text-amber-600 dark:text-amber-400';
}

function WorkerUnitCell({
  unit,
  row,
  stage,
  isUnitSelected,
  onToggleRecordIds,
}: {
  unit: RequirementWorkerUnit | undefined;
  row: ApprovalRequirementRow;
  stage: 'manager' | 'cluster';
  isUnitSelected: boolean;
  onToggleRecordIds: (ids: string[]) => void;
}) {
  if (!unit) return <span className="text-muted-foreground">-</span>;
  const currentStatus = stage === 'manager' ? unit.managerStatus : unit.clusterStatus;
  const otherStatus = stage === 'manager' ? unit.clusterStatus : unit.managerStatus;
  const otherStageLabel = stage === 'manager' ? 'CH' : 'PM';
  const decided = currentStatus === 'APPROVED' || currentStatus === 'REJECTED';
  const remarks = stage === 'manager' ? unit.managerRemarks : unit.clusterRemarks;
  return (
    <label className="flex items-start gap-2 cursor-pointer text-xs font-medium text-foreground select-none">
      <input
        type="checkbox"
        checked={decided || isUnitSelected}
        disabled={decided}
        aria-label={`Select ${workerUnitLabel(unit)} (${row.requirementNumber})`}
        onChange={() => onToggleRecordIds([unit.unitKey])}
        className="mt-0.5 h-4 w-4 cursor-pointer align-middle rounded border-gray-300 disabled:cursor-default"
      />
      <span className="leading-tight">
        <span>{workerUnitLabel(unit)}</span>
        <span className="mt-0.5 flex flex-col gap-0.5 text-[10px]">
          <span className={statusClass(currentStatus)}>{stageStatusLabel(stage, currentStatus)}</span>
          <span className={statusClass(otherStatus)}>{otherStageLabel}: {statusLabel(otherStatus)}</span>
          {remarks && <span className="text-muted-foreground break-words">{remarks}</span>}
        </span>
      </span>
    </label>
  );
}

function WorkerUnitList({
  units,
  row,
  stage,
  selectedIds,
  onToggleRecordIds,
}: {
  units: RequirementWorkerUnit[];
  row: ApprovalRequirementRow;
  stage: 'manager' | 'cluster';
  selectedIds: Set<string>;
  onToggleRecordIds: (ids: string[]) => void;
}) {
  if (units.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-col gap-2">
      {units.map((unit) => (
        <WorkerUnitCell
          key={unit.unitKey}
          unit={unit}
          row={row}
          stage={stage}
          isUnitSelected={selectedIds.has(unit.unitKey)}
          onToggleRecordIds={onToggleRecordIds}
        />
      ))}
    </div>
  );
}

function ActualWorkerUnitCell({
  unit,
  row,
  stage,
  isUnitSelected,
  onToggleRecordIds,
}: {
  unit: ActualWorkerUnit | undefined;
  row: ApprovalActualRow;
  stage: 'manager' | 'cluster';
  isUnitSelected: boolean;
  onToggleRecordIds: (ids: string[]) => void;
}) {
  if (!unit) return <span className="text-muted-foreground">-</span>;
  const currentStatus = stage === 'manager' ? unit.managerStatus : unit.clusterStatus;
  const otherStatus = stage === 'manager' ? unit.clusterStatus : unit.managerStatus;
  const otherStageLabel = stage === 'manager' ? 'CH' : 'PM';
  const decided = currentStatus === 'APPROVED' || currentStatus === 'REJECTED';
  const remarks = stage === 'manager' ? unit.managerRemarks : unit.clusterRemarks;
  return (
    <label className="flex items-start gap-2 cursor-pointer text-xs font-medium text-foreground select-none">
      <input
        type="checkbox"
        checked={decided || isUnitSelected}
        disabled={decided}
        aria-label={`Select ${actualWorkerUnitLabel(unit)} (${row.actualNumber})`}
        onChange={() => onToggleRecordIds([unit.unitKey])}
        className="mt-0.5 h-4 w-4 cursor-pointer align-middle rounded border-gray-300 disabled:cursor-default"
      />
      <span className="leading-tight">
        <span>{actualWorkerUnitLabel(unit)}</span>
        <span className="mt-0.5 flex flex-col gap-0.5 text-[10px]">
          <span className={statusClass(currentStatus)}>{stageStatusLabel(stage, currentStatus)}</span>
          <span className={statusClass(otherStatus)}>{otherStageLabel}: {statusLabel(otherStatus)}</span>
          {remarks && <span className="text-muted-foreground break-words">{remarks}</span>}
        </span>
      </span>
    </label>
  );
}

function PhotoCell({ photos }: { photos: BlockReportPhoto[] }) {
  if (!photos || photos.length === 0) return null;
  const shown = photos.slice(0, 4);
  return (
    <div className="flex flex-wrap items-center justify-center gap-1">
      {shown.map((p) => (
        <AuthenticatedPhoto
          key={p.photo_id}
          src={`/actual-status/${p.entity_id}/photographs/${p.photo_id}`}
          alt={p.file_name || 'Work photograph'}
          className="block h-[84px] w-[110px] border border-black bg-white object-contain p-0.5"
          placeholderClassName="h-[84px] w-[110px]"
        />
      ))}
    </div>
  );
}

function ActualCheckCell({ row, selectedIds, onToggle }: { row: ApprovalActualRow; selectedIds: Set<string>; onToggle: (ids: string[]) => void }) {
  const checked = row.recordIds.some((id) => selectedIds.has(id));
  const partialChecked = checked && !row.recordIds.every((id) => selectedIds.has(id));
  return (
    <td className="print-check-cell">
      <ApproveCheckbox
        checked={checked}
        indeterminate={partialChecked}
        disabled={row.recordIds.length === 0}
        label={`Select ${row.blockName}`}
        onChange={() => onToggle(row.recordIds)}
      />
    </td>
  );
}

export function ApprovalRequirementSheet({
  projectName,
  rows,
  selectedIds,
  onToggleRecordIds,
  allSelected,
  partial,
  onToggleAll,
  stage = 'manager',
}: ApprovalSheetProps & { rows: ApprovalRequirementRow[] }) {
  return (
    <div className="print-report-sheet pending-approval-report overflow-x-auto">
      <table className="print-table w-full min-w-[1080px]">
        <colgroup>
          <col style={{ width: '4%' }} />
          <col style={{ width: '12%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '12%' }} />
        </colgroup>
        <thead>
          <tr>
            <th className="print-title" colSpan={9}>APPROVAL OF LABOUR REQUIREMENT REPORT FORMAT</th>
          </tr>
          <tr>
            <td className="print-subhead" colSpan={9}>Project: {projectName}</td>
          </tr>
          <tr>
            <th rowSpan={2} className="print-index-cell">#</th>
            <th rowSpan={2}>APPROVAL CHECK/SELECTION BLOCK</th>
            <th rowSpan={2}>Date</th>
            <th rowSpan={2}>Block</th>
            <th colSpan={3}>REQUIRED LABOUR</th>
            <th rowSpan={2}>LOCATION OF WORK</th>
            <th rowSpan={2}>DESCRIPTION OF WORK</th>
          </tr>
          <tr>
            <th>SKILLED</th>
            <th>SEMI-SKILLED</th>
            <th>UN-SKILLED</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={9} className="text-center py-6 text-muted-foreground text-sm">
                No pending requirements.
              </td>
            </tr>
          ) : (
            rows.map((row, index) => {
              const categoryGroups = groupWorkerUnitsByCategory(row.workerUnits);
              const lineCount = Math.max(categoryGroups.length, 1);
              const selectedSelectableUnits = row.recordIds.filter((id) => selectedIds.has(id));
              const isReqSelected = row.recordIds.length > 0 && selectedSelectableUnits.length === row.recordIds.length;
              const isIndeterminate = !isReqSelected && selectedSelectableUnits.length > 0;
              const decidedCount = stage === 'manager'
                ? row.managerDecidedUnitsCount
                : row.clusterDecidedUnitsCount;
              const totalUnits = Math.max(row.totalUnits, row.workerUnits.length);
              const rowLabel = `${row.blockName} ${row.requirementNumber}`;

              return Array.from({ length: lineCount }).map((_, lineIdx) => {
                const group = categoryGroups[lineIdx];
                const skilledUnits = group?.unitsBySkill.SKILLED ?? [];
                const semiSkilledUnits = group?.unitsBySkill.SEMI_SKILLED ?? [];
                const unskilledUnits = group?.unitsBySkill.UNSKILLED ?? [];
                const lineUnits = [...skilledUnits, ...semiSkilledUnits, ...unskilledUnits];
                const isFirst = lineIdx === 0;
                const isUnitSelected = lineUnits.some((u) => selectedIds.has(u.unitKey));
                const isLineHighlighted = isUnitSelected;

                return (
                  <tr
                    key={`${row.key}-category-${group?.key ?? 'empty'}-${lineIdx}`}
                    data-requirement-id={row.requirementId}
                    data-line-index={lineIdx}
                    data-category-key={group?.key}
                    className={isLineHighlighted ? 'print-row-selected' : undefined}
                  >
                    {isFirst && (
                      <td rowSpan={lineCount} className="print-index-cell align-top">
                        {rowSerial(index)}
                      </td>
                    )}
                    {isFirst && (
                      <td rowSpan={lineCount} className="print-check-cell align-top">
                        <div className="flex flex-col items-center justify-center gap-1 py-1">
                          <ApproveCheckbox
                            checked={isReqSelected}
                            indeterminate={isIndeterminate}
                            disabled={row.recordIds.length === 0}
                            label={`Select ${rowLabel}`}
                            onChange={() => onToggleRecordIds(row.recordIds)}
                          />
                          <span className="text-[11px] font-semibold text-primary text-center leading-tight">
                            {row.requirementNumber}
                          </span>
                          {(row.revisionNumber ?? 0) > 0 && (
                            <span className="rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-800" title={formatRevisedAt(row.revisedAt)}>
                              Revised R{row.revisionNumber}{row.revisedBy ? ` by ${row.revisedBy}` : ''}
                            </span>
                          )}
                          <span className="text-[10px] text-muted-foreground text-center leading-tight">
                            Overall
                          </span>
                          {totalUnits > 0 && (
                            <span className="text-[10px] text-muted-foreground text-center leading-tight">
                              {decidedCount}/{totalUnits} entries decided
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                    {isFirst && (
                      <td rowSpan={lineCount} className="print-date-cell align-top whitespace-nowrap">
                        {row.workDate || ''}
                      </td>
                    )}
                    {isFirst && (
                      <td rowSpan={lineCount} className="print-block-cell align-top font-medium">
                        {row.blockName || '—'}
                      </td>
                    )}

                    {/* SKILLED Column */}
                    <td data-skill-bucket="SKILLED" className="print-worker-cell align-middle">
                      <WorkerUnitList
                        units={skilledUnits}
                        row={row}
                        stage={stage}
                        selectedIds={selectedIds}
                        onToggleRecordIds={onToggleRecordIds}
                      />
                    </td>

                    {/* SEMI-SKILLED Column */}
                    <td data-skill-bucket="SEMI_SKILLED" className="print-worker-cell align-middle">
                      <WorkerUnitList
                        units={semiSkilledUnits}
                        row={row}
                        stage={stage}
                        selectedIds={selectedIds}
                        onToggleRecordIds={onToggleRecordIds}
                      />
                    </td>

                    {/* UN-SKILLED Column */}
                    <td data-skill-bucket="UNSKILLED" className="print-worker-cell align-middle">
                      <WorkerUnitList
                        units={unskilledUnits}
                        row={row}
                        stage={stage}
                        selectedIds={selectedIds}
                        onToggleRecordIds={onToggleRecordIds}
                      />
                    </td>

                    {/* LOCATION OF WORK Column (rendered once per requirement with rowSpan) */}
                    {isFirst && (
                      <td rowSpan={lineCount} className="print-location-cell align-top text-xs">
                        {row.locationOfWork || '—'}
                      </td>
                    )}

                    {/* DESCRIPTION OF WORK Column: one merged cell per requirement, preserving line breaks */}
                    {isFirst && (
                      <td rowSpan={lineCount} className="align-top text-xs whitespace-pre-wrap break-words">
                        {row.descriptionOfWork || '—'}
                      </td>
                    )}
                  </tr>
                );
              })
            })
          )}
        </tbody>
      </table>
      <div className="mt-2 flex items-center gap-2">
        <ApproveCheckbox
          checked={allSelected}
          indeterminate={partial}
          disabled={!rows.some((r) => r.recordIds.length > 0)}
          label="Select all blocks"
          onChange={onToggleAll}
        />
        <span className="text-xs text-secondary">Select all pending requirements for this project and report</span>
      </div>
    </div>
  );
}

export function ApprovalActualSheet({ projectName, rows, selectedIds, onToggleRecordIds, allSelected, partial, onToggleAll, stage = 'manager' }: ApprovalSheetProps & { rows: ApprovalActualRow[] }) {
  return (
    <div className="print-report-sheet pending-approval-report overflow-x-auto">
      <table className="print-table w-full min-w-[1280px]">
        <colgroup>
          <col style={{ width: '4%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '7%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '5%' }} />
        </colgroup>
        <thead>
          <tr>
            <th className="print-title" colSpan={13}>ACTUAL LABOUR RECEIVED REPORT FORMAT</th>
          </tr>
          <tr>
            <td className="print-subhead" colSpan={13}>Project: {projectName}</td>
          </tr>
          <tr>
            <th rowSpan={2} className="print-index-cell">#</th>
            <th rowSpan={2}>REVIEW CHECK/SELECTION BLOCK</th>
            <th rowSpan={2}>Date</th>
            <th rowSpan={2}>Block</th>
            <th colSpan={3}>ACTUAL LABOUR</th>
            <th rowSpan={2}>TYPE OF WORK</th>
            <th rowSpan={2}>LOCATION OF WORK</th>
            <th rowSpan={2}>DESCRIPTION OF WORK</th>
            <th rowSpan={2}>ACTUAL WORK DONE DISCRIPTION</th>
            <th rowSpan={2}>PHOTOGRAPH OF ACTUAL WORK DONE</th>
            <th rowSpan={2}>REMARKS IF ANY</th>
          </tr>
          <tr>
            <th>SKILLED</th>
            <th>SEMI-SKILLED</th>
            <th>UN-SKILLED</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={13} className="text-center py-6 text-muted-foreground text-sm">
                No pending actual labour records.
              </td>
            </tr>
          ) : (
            rows.map((row, index) => {
              const unitsBySkill = {
                SKILLED: row.workerUnits.filter((u) => u.skillBucket === 'SKILLED'),
                SEMI_SKILLED: row.workerUnits.filter((u) => u.skillBucket === 'SEMI_SKILLED'),
                UNSKILLED: row.workerUnits.filter((u) => u.skillBucket === 'UNSKILLED'),
              };
              const lineCount = Math.max(
                unitsBySkill.SKILLED.length,
                unitsBySkill.SEMI_SKILLED.length,
                unitsBySkill.UNSKILLED.length,
                row.descriptionLines.length,
                1,
              );
              const isActualSelected = selectedIds.has(row.actualId);
              const selectedUnits = row.workerUnits.filter((u) => selectedIds.has(u.unitKey));
              const allUnitsSelected = row.workerUnits.length > 0 && selectedUnits.length === row.workerUnits.length;
              const someUnitsSelected = selectedUnits.length > 0;
              const isIndeterminate = isActualSelected || allUnitsSelected ? false : someUnitsSelected;
              const decidedCount = stage === 'manager'
                ? row.managerDecidedUnitsCount
                : row.clusterDecidedUnitsCount;
              const totalUnits = Math.max(row.totalUnits, row.workerUnits.length);
              return Array.from({ length: lineCount }).map((_, lineIdx) => {
                const skilledUnit = unitsBySkill.SKILLED[lineIdx];
                const semiSkilledUnit = unitsBySkill.SEMI_SKILLED[lineIdx];
                const unskilledUnit = unitsBySkill.UNSKILLED[lineIdx];
                const lineUnits = [skilledUnit, semiSkilledUnit, unskilledUnit].filter(Boolean) as ActualWorkerUnit[];
                const descText = row.descriptionLines[lineIdx];
                const isFirst = lineIdx === 0;
                const isUnitSelected = lineUnits.some((u) => selectedIds.has(u.unitKey) || isActualSelected);
                const isLineHighlighted = isActualSelected || isUnitSelected;

                return (
                  <tr
                    key={`${row.key}-line-${lineIdx}`}
                    data-actual-id={row.actualId}
                    data-line-index={lineIdx}
                    className={isLineHighlighted ? 'print-row-selected' : undefined}
                  >
                    {isFirst && (
                      <td rowSpan={lineCount} className="print-index-cell align-top">
                        {rowSerial(index)}
                      </td>
                    )}
                    {isFirst && (
                      <td rowSpan={lineCount} className="print-check-cell align-top">
                        <div className="flex flex-col items-center justify-center gap-1 py-1">
                          <ApproveCheckbox
                            checked={isActualSelected}
                            indeterminate={isIndeterminate}
                            label={`Select ${row.blockName}`}
                            onChange={() => onToggleRecordIds([row.actualId])}
                          />
                          <span className="text-[11px] font-semibold text-primary text-center leading-tight">
                            {row.actualNumber}
                          </span>
                          {(row.revisionNumber ?? 0) > 0 && (
                            <span className="rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-800" title={formatRevisedAt(row.revisedAt)}>
                              Revised R{row.revisionNumber}{row.revisedBy ? ` by ${row.revisedBy}` : ''}
                            </span>
                          )}
                          <span className="text-[10px] text-muted-foreground text-center leading-tight">
                            Overall
                          </span>
                          {totalUnits > 0 && (
                            <span className="text-[10px] text-muted-foreground text-center leading-tight">
                              {decidedCount}/{totalUnits} entries decided
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                    {isFirst && (
                      <td rowSpan={lineCount} className="print-date-cell align-top whitespace-nowrap">
                        {row.workDate || ''}
                      </td>
                    )}
                    {isFirst && (
                      <td rowSpan={lineCount} className="print-block-cell align-top font-medium">
                        {row.blockName || '-'}
                      </td>
                    )}

                    {/* SKILLED Column */}
                    <td data-skill-bucket="SKILLED" className="print-worker-cell align-middle">
                      {skilledUnit ? (
                        <ActualWorkerUnitCell
                          unit={skilledUnit}
                          row={row}
                          stage={stage}
                          isUnitSelected={selectedIds.has(skilledUnit.unitKey) || isActualSelected}
                          onToggleRecordIds={onToggleRecordIds}
                        />
                      ) : (
                        <span className="text-muted-foreground">
                          {row.workerUnits.length === 0 && isFirst ? numberCell(row.actualSkilled) : '-'}
                        </span>
                      )}
                    </td>

                    {/* SEMI-SKILLED Column */}
                    <td data-skill-bucket="SEMI_SKILLED" className="print-worker-cell align-middle">
                      {semiSkilledUnit ? (
                        <ActualWorkerUnitCell
                          unit={semiSkilledUnit}
                          row={row}
                          stage={stage}
                          isUnitSelected={selectedIds.has(semiSkilledUnit.unitKey) || isActualSelected}
                          onToggleRecordIds={onToggleRecordIds}
                        />
                      ) : (
                        <span className="text-muted-foreground">
                          {row.workerUnits.length === 0 && isFirst ? numberCell(row.actualSemiSkilled) : '-'}
                        </span>
                      )}
                    </td>

                    {/* UN-SKILLED Column */}
                    <td data-skill-bucket="UNSKILLED" className="print-worker-cell align-middle">
                      {unskilledUnit ? (
                        <ActualWorkerUnitCell
                          unit={unskilledUnit}
                          row={row}
                          stage={stage}
                          isUnitSelected={selectedIds.has(unskilledUnit.unitKey) || isActualSelected}
                          onToggleRecordIds={onToggleRecordIds}
                        />
                      ) : (
                        <span className="text-muted-foreground">
                          {row.workerUnits.length === 0 && isFirst ? numberCell(row.actualUnskilled) : '-'}
                        </span>
                      )}
                    </td>

                    {isFirst && (
                      <td rowSpan={lineCount} className="align-top text-xs break-words">
                        {row.typeOfWork || '-'}
                      </td>
                    )}
                    {isFirst && (
                      <td rowSpan={lineCount} className="print-location-cell align-top text-xs">
                        {row.locationOfWork || '-'}
                      </td>
                    )}
                    {row.descriptionLines.length <= 1 ? (
                      isFirst && (
                        <td rowSpan={lineCount} className="align-top text-xs break-words">
                          {row.descriptionLines[0] || row.descriptionOfWork || '-'}
                        </td>
                      )
                    ) : (
                      <td className="align-middle text-xs break-words">
                        {descText || '-'}
                      </td>
                    )}
                    {isFirst && (
                      <td rowSpan={lineCount} className="align-top text-xs break-words">
                        {row.actualWorkDone || '-'}
                      </td>
                    )}
                    {isFirst && (
                      <td rowSpan={lineCount} className="align-top">
                        <PhotoCell photos={row.photos} />
                      </td>
                    )}
                    {isFirst && (
                      <td rowSpan={lineCount} className="align-top text-xs break-words">
                        {row.remarks || '-'}
                      </td>
                    )}
                  </tr>
                );
              });
            })
          )}
        </tbody>
      </table>
      <div className="mt-2 flex items-center gap-2">
        <ApproveCheckbox
          checked={allSelected}
          indeterminate={partial}
          disabled={!rows.some((r) => r.recordIds.length > 0)}
          label="Select all blocks"
          onChange={onToggleAll}
        />
        <span className="text-xs text-secondary">Select all pending actual labour rows for this project and report</span>
      </div>
    </div>
  );
}
